import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Database, Pause, Settings, Sparkles } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  readPrefs,
  sourcePageApi,
  writePrefs,
  type Direction,
  type Grain,
  type OverviewParams,
  type SourceHeader,
  type SourcePageOverview,
  type SyncRun,
} from '../../services/source-page-api';
import SeriesChart from './overview/SeriesChart';
import { attentionDisplay } from './sourceDisplay';
import { formatInstant, formatPlain, instantToPlainDate, zoneAbbreviation } from '../../utils/temporal';

const GRAIN_LABELS: Record<Grain, string> = {
  month: 'By month · last 12',
  week: 'By week · last 12',
  day: 'By day · last 30',
  hour: 'By hour · today',
};

export default function SourceOverviewPage() {
  const { sourceId = '' } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const [params, setParams] = useState<OverviewParams>(() => readPrefs(sourceId));
  const [data, setData] = useState<SourcePageOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tableView, setTableView] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    sourcePageApi
      .getOverview(sourceId, params)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? 'notfound' : 'failed');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [sourceId, params]);

  const update = useCallback(
    (next: Partial<OverviewParams>) => {
      setParams((prev) => {
        const merged = { ...prev, ...next };
        writePrefs(sourceId, merged);
        return merged;
      });
    },
    [sourceId],
  );

  const money = useCallback(
    (n: number, unit: string | null) =>
      unit && unit.length === 3
        ? new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: unit,
            maximumFractionDigits: n >= 1000 ? 0 : 2,
          }).format(n)
        : new Intl.NumberFormat(undefined, {
            maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
          }).format(n),
    [],
  );

  const chartFormat = useMemo(() => {
    const unit = data?.series?.unit ?? null;
    return (n: number) => compact(n, unit);
  }, [data?.series?.unit]);

  if (loading && !data) return <PageShell><Skeleton /></PageShell>;

  if (error === 'notfound') {
    return (
      <PageShell>
        <EmptyCard
          title="Source not found"
          body="It may have been disconnected, or you may not have access to it."
          action={{ label: 'Back to data sources', onClick: () => navigate('/sources') }}
        />
      </PageShell>
    );
  }
  if (error || !data) {
    return (
      <PageShell>
        <EmptyCard
          title="Couldn't load this source"
          body="Something went wrong fetching the overview. Try again in a moment."
          action={{ label: 'Retry', onClick: () => setParams((p) => ({ ...p })) }}
        />
      </PageShell>
    );
  }

  const { source, scope, coverage, runs, states, kpis, series, leaderboard, recentRecords, pulse } =
    data;
  const zero = states.includes('zero_records');

  return (
    <PageShell>
      <div className="flex flex-col lg:flex-row gap-5 items-start">
        {/* ---------- Sticky rail: connection, sync, history held ---------- */}
        <aside className="w-full lg:w-[268px] lg:sticky lg:top-4 shrink-0 rounded-xl border border-border bg-card overflow-hidden">
          <div className="p-4 border-b border-border">
            <button
              onClick={() => navigate('/sources')}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-3"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Data sources
            </button>
            <h1 className="text-base font-semibold text-foreground leading-tight">{source.name}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{source.organizationName}</p>
          </div>

          <RailSection label="Connection">
            <div className="flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full ${
                  source.healthStatus === 'healthy' ? 'bg-primary' : 'bg-destructive'
                }`}
              />
              <span className="text-sm font-semibold text-foreground">
                {source.healthStatus === 'healthy' ? 'Healthy' : 'Needs attention'}
              </span>
            </div>
            {runs.length > 0 && (
              <>
                <div className="flex gap-[3px] mt-2.5">
                  {[...runs].reverse().map((r, i) => {
                    const tone = r.ok ? 'bg-primary' : 'bg-destructive';
                    const label = `${new Date(r.startedAt).toLocaleString()} — ${runTickLabel(r)}`;
                    const href = runHref(source, r);
                    return href ? (
                      <Link
                        key={r.runId ?? i}
                        to={href}
                        title={label}
                        aria-label={label}
                        className={`w-1.5 h-4 rounded-sm hover:opacity-70 ${tone}`}
                      />
                    ) : (
                      <span key={i} title={label} aria-hidden className={`w-1.5 h-4 rounded-sm ${tone}`} />
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  Last {runs.length} runs · {runs.filter((r) => r.ok).length} clean
                  {runs.some((r) => !r.ok) &&
                    `, ${runs.filter((r) => !r.ok).length} need attention`}
                </p>
              </>
            )}
            {source.healthStatus !== 'healthy' && (
              <button
                onClick={() => navigate('/sources')}
                className="mt-2 text-[11px] font-semibold text-primary hover:underline"
              >
                Open source settings →
              </button>
            )}
          </RailSection>

          <RailSection label="Last sync">
            <p className="text-sm font-semibold text-foreground">{relative(source.lastSyncAt)}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {source.lastSyncAt ? new Date(source.lastSyncAt).toLocaleString() : 'Never run'}
              {runs[0] ? ` · ${runs[0].recordsCreated} new records` : ''}
            </p>
          </RailSection>

          <RailSection label="Next sync">
            {!source.syncEnabled ? (
              <div className="flex items-center gap-2">
                <Pause className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Paused</p>
                  <p className="text-[11px] text-muted-foreground">Resumes from source settings</p>
                </div>
              </div>
            ) : source.nextSyncAt ? (
              <>
                <p className="text-sm font-semibold text-foreground">
                  {relativeFuture(source.nextSyncAt)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {new Date(source.nextSyncAt).toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                  {cadence(source.syncFrequency)}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-foreground">Live</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Last received {relative(source.lastSyncAt)}
                </p>
              </>
            )}
          </RailSection>

          <RailSection label="History held">
            <RailStat label="Coverage" value={coverage.months > 0 ? `${coverage.months} months` : '—'} />
            <RailStat label="Earliest record" value={dateOnly(coverage.earliestRecord)} />
            <RailStat label="Records stored" value={coverage.totalRecords.toLocaleString()} />
            <RailStat label="Added today" value={`+${coverage.addedToday}`} />
            <RailStat
              label="Gaps detected"
              value={coverage.gapDays === 0 ? 'None' : `${coverage.gapDays} days`}
              tone={coverage.gapDays === 0 ? 'good' : 'warn'}
            />
          </RailSection>

          {scope.accounts.length > 0 && (
            <RailSection label="Accounts">
              {scope.accounts.map((a) => (
                <div key={a.id} className="flex items-center gap-2 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <span className="text-[12.5px] text-foreground truncate flex-1">
                    {a.name ?? 'Account'}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{a.type ?? ''}</span>
                </div>
              ))}
            </RailSection>
          )}

          <div className="p-3 flex flex-col gap-2">
            <button
              onClick={() => navigate(`/chat?source=${source.id}`)}
              className="w-full rounded-lg bg-primary text-primary-foreground text-[13px] font-semibold py-2 hover:opacity-90"
            >
              Ask the AI about this source
            </button>
            <button
              onClick={() => navigate('/sources')}
              className="w-full rounded-lg border border-border text-foreground text-[13px] font-semibold py-2 hover:bg-accent inline-flex items-center justify-center gap-1.5"
            >
              <Settings className="w-3.5 h-3.5" /> Source settings
            </button>
          </div>
        </aside>

        {/* ---------- Scrollable column ---------- */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {(scope.accounts.length > 1 || scope.partition || scope.direction) && (
            <section className="rounded-xl border border-border bg-card p-3.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Showing
                </span>

                {scope.accounts.length > 1 && (
                  <Select
                    value={params.accountId ?? (scope.rollupAllowed ? 'all' : scope.selectedAccountIds[0])}
                    onChange={(v) => update({ accountId: v === 'all' ? undefined : v })}
                    options={[
                      ...(scope.rollupAllowed
                        ? [{ value: 'all', label: `All accounts (${scope.accounts.length})` }]
                        : []),
                      ...scope.accounts.map((a) => ({ value: a.id, label: a.name ?? 'Account' })),
                    ]}
                  />
                )}

                {scope.partition && (
                  <Select
                    required
                    value={scope.partition.active ?? ''}
                    onChange={(v) => update({ partition: v })}
                    options={scope.partition.values.map((v) => ({
                      value: v.value,
                      label: `${scope.partition!.label}: ${v.value} · ${v.count.toLocaleString()}`,
                    }))}
                  />
                )}

                {scope.direction && (
                  <Select
                    value={scope.direction}
                    onChange={(v) => update({ direction: v as Direction })}
                    options={[
                      { value: 'out', label: 'Money out' },
                      { value: 'in', label: 'Money in' },
                    ]}
                  />
                )}

                {scope.notes.length > 0 && (
                  <p className="text-[12px] text-muted-foreground flex-1 min-w-[220px]">
                    {scope.notes.join(' ')}
                  </p>
                )}
              </div>
            </section>
          )}

          <StateBanners states={states} source={source} />

          {zero ? (
            <EmptyCard
              title="No records yet"
              body={
                source.syncEnabled
                  ? "This source is connected but hasn't stored any records yet. The first sync may still be running."
                  : 'This source is paused, so nothing is being collected. Resume it from source settings.'
              }
            />
          ) : (
            <>
              {kpis && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {kpis.map((k) => (
                    <div key={k.label} className="rounded-xl border border-border bg-card p-4">
                      <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {k.label}
                      </p>
                      <p className="text-2xl font-bold text-foreground mt-1.5 tabular-nums">
                        {money(k.value, k.unit)}
                      </p>
                      <p className="text-[11.5px] mt-1">
                        {k.deltaPct === null ? (
                          <span className="text-muted-foreground">No comparable prior period</span>
                        ) : (
                          <>
                            <span
                              className={
                                k.deltaPct >= 0 ? 'text-primary font-semibold' : 'text-destructive font-semibold'
                              }
                            >
                              {k.deltaPct >= 0 ? '▲' : '▼'} {Math.abs(k.deltaPct).toFixed(1)}%
                            </span>
                            <span className="text-muted-foreground"> vs prior period</span>
                          </>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {series && (
                <section className="rounded-xl border border-border bg-card p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <h2 className="text-sm font-semibold text-foreground">{series.metricLabel}</h2>
                    <span className="text-[11px] text-muted-foreground">
                      Master metric · {series.timezone.split('/').pop()?.replace(/_/g, ' ')} time
                      {zoneAbbreviation(series.timezone) && ` (${zoneAbbreviation(series.timezone)})`}
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      <Select
                        value={series.grain}
                        onChange={(v) => update({ grain: v as Grain })}
                        options={(Object.keys(GRAIN_LABELS) as Grain[]).map((g) => ({
                          value: g,
                          label: GRAIN_LABELS[g],
                        }))}
                      />
                      <button
                        onClick={() => setTableView((t) => !t)}
                        className="rounded-lg border border-border px-2.5 py-1.5 text-[12px] font-semibold text-foreground hover:bg-accent"
                      >
                        {tableView ? 'Chart' : 'Table'}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mb-2 text-[11.5px]">
                    <LegendSwatch color={isDark ? '#4FC1ED' : '#1FA5DC'} label={series.currentLabel} />
                    {series.priorAvailable && (
                      <LegendSwatch color={isDark ? '#4A7B92' : '#93B4C6'} label={series.priorLabel} />
                    )}
                    <LegendSwatch
                      color={isDark ? '#4FC1ED' : '#1FA5DC'}
                      label="In progress"
                      faded
                    />
                    {!series.priorAvailable && (
                      <span className="ml-auto text-muted-foreground">
                        Not enough history yet to compare with {series.priorLabel.toLowerCase()}
                      </span>
                    )}
                  </div>

                  {tableView ? (
                    <SeriesTable series={series} format={(n) => money(n, series.unit)} />
                  ) : (
                    <SeriesChart series={series} isDark={isDark} formatValue={chartFormat} />
                  )}
                </section>
              )}

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {leaderboard && (
                  <section className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <h2 className="text-sm font-semibold text-foreground">Leaderboard</h2>
                      <span className="text-[11px] text-muted-foreground">Last 30 days</span>
                      <div className="ml-auto">
                        <Select
                          value={leaderboard.dimension.slug}
                          onChange={() => undefined}
                          disabled
                          options={[
                            { value: leaderboard.dimension.slug, label: leaderboard.dimension.label },
                          ]}
                        />
                      </div>
                    </div>
                    {leaderboard.available ? (
                      <>
                        <ol className="flex flex-col gap-2.5">
                          {leaderboard.rows.map((r, i) => {
                            const max = Math.max(...leaderboard.rows.map((x) => x.value), 1);
                            return (
                              <li key={r.label}>
                                <div className="flex items-baseline gap-2">
                                  <span className="text-[11px] text-muted-foreground w-3">{i + 1}</span>
                                  <span className="text-[13px] font-semibold text-foreground truncate flex-1">
                                    {r.label}
                                  </span>
                                  <span className="text-[13px] font-semibold text-foreground tabular-nums">
                                    {money(r.value, leaderboard.unit)}
                                  </span>
                                  {r.deltaPct !== null && (
                                    <span
                                      className={`text-[11px] font-semibold tabular-nums w-14 text-right ${
                                        r.deltaPct >= 0 ? 'text-primary' : 'text-destructive'
                                      }`}
                                    >
                                      {r.deltaPct >= 0 ? '▲' : '▼'} {Math.abs(r.deltaPct).toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                                <div className="h-1.5 rounded-full bg-muted mt-1 ml-5 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-primary"
                                    style={{ width: `${Math.max(2, (r.value / max) * 100)}%` }}
                                  />
                                </div>
                              </li>
                            );
                          })}
                        </ol>
                        {leaderboard.tail && (
                          <p className="text-[12px] text-muted-foreground mt-3 pt-2.5 border-t border-border">
                            Everything else ·{' '}
                            <span className="font-semibold text-foreground">
                              {money(leaderboard.tail.value, leaderboard.unit)}
                            </span>{' '}
                            across {leaderboard.tail.count} more
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-[12.5px] text-muted-foreground py-6">{leaderboard.message}</p>
                    )}
                  </section>
                )}

                {recentRecords && (
                  <section className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <h2 className="text-sm font-semibold text-foreground">Last 5 records</h2>
                      <button
                        onClick={() => navigate(`/viewer?account=${scope.selectedAccountIds[0]}`)}
                        className="ml-auto text-[12px] font-semibold text-primary hover:underline"
                      >
                        View all →
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[12.5px]">
                        <thead>
                          <tr className="text-left text-[10.5px] uppercase tracking-wide text-muted-foreground">
                            {recentRecords.columns.map((c) => (
                              <th key={c.slug} className="font-semibold pb-2 pr-3 whitespace-nowrap">
                                {c.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {recentRecords.rows.map((row) => (
                            <tr key={row.id} className="border-t border-border">
                              {recentRecords.columns.map((c) => (
                                <td
                                  key={c.slug}
                                  className="py-2 pr-3 text-foreground whitespace-nowrap max-w-[180px] truncate"
                                >
                                  {c.slug === 'timestamp'
                                    ? formatTime(row.timestamp, source.timezone)
                                    : formatCell(row.values[c.slug], c.dataType)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {recentRecords.rows.length === 0 && (
                        <p className="text-[12.5px] text-muted-foreground py-4">
                          No records in this scope.
                        </p>
                      )}
                    </div>
                  </section>
                )}
              </div>

              {pulse.length > 0 && (
                <section className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <h2 className="text-sm font-semibold text-foreground">Pulse findings</h2>
                  </div>
                  <ul className="flex flex-col gap-2">
                    {pulse.map((p) => (
                      <li
                        key={p.resultId}
                        className="flex items-center gap-3 py-2 border-t border-border first:border-0"
                      >
                        <span
                          className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                            p.severity === 'high'
                              ? 'bg-destructive text-destructive-foreground'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {p.severity}
                        </span>
                        <span className="text-[13px] text-foreground flex-1 truncate">{p.headline}</span>
                        {p.impact !== null && (
                          <span className="text-[12.5px] font-semibold text-foreground tabular-nums">
                            {money(p.impact, null)}
                            {p.impactIsEstimate && (
                              <span className="text-muted-foreground font-normal"> est.</span>
                            )}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------

function PageShell({ children }: { children: React.ReactNode }) {
  return <div className="p-4 md:p-6 max-w-[1400px] mx-auto">{children}</div>;
}

function RailSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="p-4 border-b border-border">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {label}
      </p>
      {children}
    </div>
  );
}

function RailStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'good' | 'warn';
}) {
  return (
    <div className="flex items-baseline justify-between py-[3px]">
      <span className="text-[12px] text-muted-foreground">{label}</span>
      <span
        className={`text-[12.5px] font-semibold tabular-nums ${
          tone === 'good' ? 'text-primary' : tone === 'warn' ? 'text-destructive' : 'text-foreground'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function LegendSwatch({ color, label, faded }: { color: string; label: string; faded?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <span
        className="w-2.5 h-2.5 rounded-sm"
        style={{
          backgroundColor: color,
          opacity: faded ? 0.45 : 1,
          border: faded ? `2px solid ${color}` : undefined,
        }}
      />
      {label}
    </span>
  );
}

function StateBanners({
  states,
  source,
}: {
  states: SourcePageOverview['states'];
  source: SourcePageOverview['source'];
}) {
  const banners: Array<{ icon: React.ReactNode; text: string }> = [];
  if (states.includes('backfill_in_progress'))
    banners.push({
      icon: <Database className="w-4 h-4" />,
      text: 'Backfill in progress — history is still loading, so comparisons are incomplete.',
    });
  if (states.includes('unhealthy'))
    banners.push({
      icon: <AlertTriangle className="w-4 h-4" />,
      text: attentionDisplay(source).message,
    });
  if (states.includes('paused') && !states.includes('zero_records'))
    banners.push({
      icon: <Pause className="w-4 h-4" />,
      text: 'Syncing is paused — the numbers below stop at the last sync.',
    });
  if (!banners.length) return null;
  return (
    <div className="flex flex-col gap-2">
      {banners.map((b, i) => (
        <div
          key={i}
          className="flex items-center gap-2.5 rounded-xl border border-border bg-muted px-3.5 py-2.5 text-[12.5px] text-foreground"
        >
          <span className="text-muted-foreground">{b.icon}</span>
          {b.text}
        </div>
      ))}
    </div>
  );
}

function SeriesTable({
  series,
  format,
}: {
  series: NonNullable<SourcePageOverview['series']>;
  format: (n: number) => string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-left text-[10.5px] uppercase tracking-wide text-muted-foreground">
            <th className="font-semibold pb-2 pr-3">Period</th>
            <th className="font-semibold pb-2 pr-3 text-right">{series.currentLabel}</th>
            {series.priorAvailable && (
              <th className="font-semibold pb-2 pr-3 text-right">{series.priorLabel}</th>
            )}
            <th className="font-semibold pb-2" />
          </tr>
        </thead>
        <tbody>
          {series.buckets.map((b) => (
            <tr key={b.start} className="border-t border-border">
              <td className="py-1.5 pr-3 text-foreground">
                {formatPlain(b.start, {
                  month: 'short',
                  ...(series.grain === 'month' ? { year: '2-digit' } : { day: 'numeric' }),
                })}
              </td>
              <td className="py-1.5 pr-3 text-right text-foreground tabular-nums">
                {format(b.current)}
              </td>
              {series.priorAvailable && (
                <td className="py-1.5 pr-3 text-right text-muted-foreground tabular-nums">
                  {b.prior === null ? '—' : format(b.prior)}
                </td>
              )}
              <td className="py-1.5">
                {b.partial && (
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    partial · {b.shortfall}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
  required,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={`rounded-lg border px-2.5 py-1.5 text-[12.5px] font-semibold text-foreground bg-card disabled:opacity-70 ${
        required ? 'border-primary bg-accent' : 'border-border'
      }`}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function EmptyCard({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-8 text-center">
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="text-[12.5px] text-muted-foreground mt-1.5 max-w-md mx-auto">{body}</p>
      {action && (
        <button
          onClick={action.onClick}
          className="mt-4 rounded-lg bg-primary text-primary-foreground text-[13px] font-semibold px-4 py-2 hover:opacity-90"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="flex flex-col lg:flex-row gap-5">
      <div className="w-full lg:w-[268px] h-[520px] rounded-xl bg-muted animate-pulse" />
      <div className="flex-1 flex flex-col gap-4">
        <div className="h-14 rounded-xl bg-muted animate-pulse" />
        <div className="h-24 rounded-xl bg-muted animate-pulse" />
        <div className="h-[340px] rounded-xl bg-muted animate-pulse" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * A push-fed run is one delivered file, so its tick opens that run. Scheduled
 * syncs have nothing to open — they stay a plain mark.
 */
function runHref(source: SourceHeader, run: SyncRun): string | null {
  if (!run.runId) return null;
  if (source.sourceType === 'inbound_file') {
    return `/integrations/inbound-file/${source.id}/edit?tab=runs&run=${run.runId}`;
  }
  return null;
}

/** "clean"/"failed" for a sync; a real lifecycle status for a delivered file. */
function runTickLabel(run: SyncRun): string {
  if (run.status) return run.status.replace(/_/g, ' ');
  return run.ok ? 'clean' : 'failed';
}

function relative(iso: string | null): string {
  if (!iso) return 'Never';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** Minute granularity, never a ticking countdown. */
function relativeFuture(iso: string): string {
  const mins = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  if (mins <= 0) return 'Due now';
  if (mins < 60) return `in ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs} hour${hrs === 1 ? '' : 's'}`;
  return `in ${Math.round(hrs / 24)} days`;
}

/**
 * `sync_frequency` holds a cron expression. Only the shapes the scheduler
 * actually emits are translated — anything else is omitted rather than shown
 * raw, because "runs * / 15 * * * *" reads as a bug to an owner.
 */
function cadence(cron: string | null): string {
  if (!cron) return '';
  const named: Record<string, string> = {
    hourly: 'hourly',
    daily: 'daily',
    weekly: 'weekly',
  };
  if (named[cron]) return ` · runs ${named[cron]}`;
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return '';
  const [min, hour, dom, , dow] = parts;
  const everyN = /^\*\/(\d+)$/;
  if (everyN.test(min) && hour === '*') {
    return ` · runs every ${min.match(everyN)![1]} minutes`;
  }
  if (everyN.test(hour)) return ` · runs every ${hour.match(everyN)![1]} hours`;
  if (/^\d+$/.test(min) && hour === '*') return ' · runs hourly';
  if (/^\d+$/.test(min) && /^\d+$/.test(hour)) {
    if (dow !== '*') return ' · runs weekly';
    if (dom !== '*') return ' · runs monthly';
    return ' · runs daily';
  }
  return '';
}

function dateOnly(plain: string | null): string {
  return formatPlain(plain, { day: 'numeric', month: 'short', year: 'numeric' });
}

function compact(n: number, unit: string | null): string {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const prefix = unit && unit.length === 3 ? currencySymbol(unit) : '';
  if (abs >= 1_000_000) return `${sign}${prefix}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${prefix}${Math.round(abs / 1_000)}K`;
  return `${sign}${prefix}${Math.round(abs)}`;
}

function currencySymbol(code: string): string {
  try {
    return (
      new Intl.NumberFormat(undefined, { style: 'currency', currency: code })
        .formatToParts(0)
        .find((p) => p.type === 'currency')?.value ?? ''
    );
  } catch {
    return '';
  }
}

/**
 * Postgres returns numeric columns as strings, so a raw render shows the stored
 * scale ("48.1800"). Trust the field map's data type rather than sniffing, so an
 * identifier that happens to look numeric is left alone.
 */
function formatCell(v: unknown, dataType: string): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  // `date` slots arrive as plain `YYYY-MM-DD` — rendering them through a Date
  // would re-zone them into the viewer's clock and lose a day.
  if (dataType === 'date' && typeof v === 'string') {
    return formatPlain(v, { day: 'numeric', month: 'short', year: 'numeric' });
  }
  if (dataType === 'currency' || dataType === 'count' || dataType === 'percentage') {
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) {
      return n.toLocaleString(undefined, {
        maximumFractionDigits: dataType === 'count' ? 0 : 2,
      });
    }
  }
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
}

/**
 * A record's timestamp is a true instant, rendered in the business timezone —
 * "6:00 PM" must mean the same shift to the owner and to an offsite bookkeeper.
 * "Today" is therefore also judged in business time.
 */
function formatTime(iso: string | null, timeZone: string | null): string {
  if (!iso) return '—';
  const sameDay =
    instantToPlainDate(iso, timeZone) === instantToPlainDate(new Date().toISOString(), timeZone);
  return formatInstant(iso, timeZone, {
    hour: 'numeric',
    minute: '2-digit',
    ...(sameDay ? {} : { month: 'short', day: 'numeric' }),
  });
}
